import { useState, useCallback, useEffect, useRef } from "react";
import axios from "../AxiosUrl";
import socketClient from "../../services/socketClient";

/**
 * AI Calling (AdminPanel) — API calls, live socket events, labels and formatters
 * shared by every page in this folder.
 *
 * Backend: BibahoBanglaBackend/routes/router.js "AI Calling (Phase 4)".
 * Errors come back as { success:false, error } with HTTP 200, or 403 { success:false, msg }
 * when the admin's role isn't allowed (Marketing settings → Access).
 */

export const getUid = () => localStorage.getItem("id");

/* ------------------------------------------------------------------ texts */

/** Guard reasons, engine reasons and error codes → plain English. */
export const REASON_TEXT = {
  // calling process / connection
  engine_offline: "The calling process isn't running on the server",
  timeout: "The calling process didn't answer in time",
  unauthorized: "The server and the calling process have different AICALL_SECRET_KEY values",
  shutting_down: "The calling process is restarting",
  at_capacity: "All channels are busy right now",
  sip_unconfigured: "The SIP line isn't set up",
  sip_not_registered: "The SIP line isn't registered",
  ai_keys_missing: "The Soniox or Claude key isn't set on the server",
  setup_failed: "The call couldn't be started",
  invalid_purpose: "Unknown call purpose",
  invalid_request: "The request was not valid",
  campaign_not_found: "Campaign not found",
  campaign_not_running: "The campaign isn't running",
  campaign_log_not_queued: "That member isn't waiting to be called",
  invalid_campaign_log: "Unknown campaign row",
  // guardrails (utils/AICalling/guards.js)
  ai_calling_off: "AI calling is switched off",
  outside_call_window: "Outside the call windows",
  do_not_call: "On the do-not-call list",
  called_recently: "Already called recently",
  daily_budget_reached: "Daily AI-call budget reached",
  invalid_number: "Not a valid Bangladeshi mobile number",
  opted_out: "Turned promotional messages off",
  no_intent: "Never started a checkout and has no package",
  women_rule: "Woman member who didn't start a checkout herself",
  paid_since: "Paid since the campaign started",
  has_package: "Already has a package",
  expired: "Waited too long, not called",
  unverified_number: "Mobile number not verified",
  not_a_member: "Not a member",
  quiet_hours: "Quiet hours",
  number_mismatch: "Number doesn't match the member",
  inactive_account: "Account deactivated or blocked",
  stopped: "Campaign was stopped",
};

/** Turns an error code (or a sentence from the server) into text for the UI. */
export const reasonText = (code) => {
  if (code === undefined || code === null || code === "") return "";
  const value = String(code);
  if (REASON_TEXT[value]) return REASON_TEXT[value];
  if (/^[a-z0-9]+(_[a-z0-9]+)+$/.test(value)) {
    const words = value.replace(/_/g, " ");
    return words.charAt(0).toUpperCase() + words.slice(1);
  }
  return value;
};

/** Why a call ended (aicalls.endReason). Blocked calls carry the guard reason. */
export const END_REASON_TEXT = {
  ring_timeout: "Rang out, no answer",
  max_duration: "Reached the call time limit",
  agent_ended: "The agent ended the call",
  caller_silent: "The member stopped answering (asked if they could hear, then said goodbye)",
  manual_hangup: "Hung up from the panel",
  manual_hangup_orphan: "Hung up from the panel (lost call)",
  member_hangup: "The member hung up",
  cancelled: "Cancelled before it was answered",
  engine_restart: "The calling process restarted",
  shutdown: "The calling process stopped",
  ai_unavailable: "The AI was unavailable",
  no_answer: "No answer",
  busy: "Busy",
  rejected: "Rejected",
  failed: "Failed",
};
export const endReasonText = (code) => {
  if (!code) return "";
  const value = String(code);
  if (END_REASON_TEXT[value]) return END_REASON_TEXT[value];
  if (value.startsWith("setup_error:")) return `Couldn't start: ${value.slice(12).trim()}`;
  return reasonText(value);
};

export const OUTCOMES = {
  link_sent: { label: "Link sent", color: "success" },
  callback: { label: "Callback", color: "info" },
  not_interested: { label: "Not interested", color: "default" },
  do_not_call: { label: "Do not call", color: "error" },
  support_ticket: { label: "Support ticket", color: "secondary" },
  completed: { label: "Completed", color: "primary" },
  no_answer: { label: "No answer", color: "warning" },
  busy: { label: "Busy", color: "warning" },
  rejected: { label: "Rejected", color: "warning" },
  failed: { label: "Failed", color: "error" },
  blocked: { label: "Blocked", color: "default" },
};
export const OUTCOME_KEYS = Object.keys(OUTCOMES);

export const CALL_STATES = {
  queued: { label: "Queued", color: "default" },
  dialing: { label: "Dialing", color: "info" },
  ringing: { label: "Ringing", color: "warning" },
  answered: { label: "Talking", color: "success" },
  ended: { label: "Ended", color: "default" },
  failed: { label: "Failed", color: "error" },
};
export const isCallActive = (state) => ["queued", "dialing", "ringing", "answered"].includes(state);

export const PURPOSES = {
  campaign: "Campaign",
  test: "Test call",
  ladder: "Channel ladder",
  callback: "Callback",
  support: "Support (inbound)",
  notice: "Purchase notice",
  signup: "New signup",
};
export const PURPOSE_KEYS = Object.keys(PURPOSES);

export const CAMPAIGN_STATUS = {
  draft: { label: "Draft", color: "default" },
  scheduled: { label: "Scheduled", color: "info" },
  running: { label: "Running", color: "warning" },
  paused: { label: "Paused", color: "secondary" },
  stopped: { label: "Stopped", color: "error" },
  completed: { label: "Completed", color: "success" },
};

export const SIP_STATES = {
  registered: { label: "Registered", color: "success" },
  registering: { label: "Registering…", color: "info" },
  failing: { label: "Failing", color: "error" },
  unconfigured: { label: "Not set up", color: "default" },
};

export const TOOL_LABELS = {
  send_payment_link: "Sent payment link",
  schedule_callback: "Scheduled a callback",
  give_special_offer: "Gave the special offer",
  do_not_call: "Do not call",
  create_support_ticket: "Support ticket",
  end_call: "Ended the call",
};
export const toolLabel = (name) => TOOL_LABELS[name] || reasonText(name) || "Tool";

/** Soniox voices (same list as the backend's models/AICallSettings.js). */
export const VOICES = ["Priya", "Kavya", "Ishita", "Nisha", "Divya", "Sneha", "Arjun", "Rohan", "Kabir", "Dev"];
export const VOICE_LABELS = {
  Priya: "Priya — female (default)", Kavya: "Kavya — female", Ishita: "Ishita — female", Nisha: "Nisha — female",
  Divya: "Divya — female", Sneha: "Sneha — female", Arjun: "Arjun — male", Rohan: "Rohan — male", Kabir: "Kabir — male", Dev: "Dev — male",
};
/** The Claude model that writes the replies (Soniox hears and speaks around it). */
export const MODELS = [
  { value: "claude-haiku-5-5", label: "Claude Haiku 5.5 — fastest (default)" },
  { value: "claude-sonnet-5-5", label: "Claude Sonnet 5.5 — smarter, slower first word" },
  { value: "claude-opus-5-5", label: "Claude Opus 5.5 — smartest, slowest and dearest" },
];
/** The quick written answer to support tickets. */
export const TICKET_MODELS = [
  { value: "claude-sonnet-5-5", label: "Claude Sonnet 5.5 (default)" },
  { value: "claude-haiku-5-5", label: "Claude Haiku 5.5 — cheapest" },
  { value: "claude-opus-5-5", label: "Claude Opus 5.5 — most careful" },
];
export const DAY_NAMES = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

export const LANDINGS = {
  checkoutProfile: "The profile they tried to unlock",
  packages: "Packages page",
  dashboard: "Dashboard",
};

/** Labels for preset options, by "preset.option", then by option name. */
const PRESET_OPTION_TEXT = {
  "abandonedCheckout.fromHours": ["From (hours ago)", "Started a checkout at least this many hours ago"],
  "abandonedCheckout.toHours": ["To (hours ago)", "…and at most this many hours ago"],
  "abandonedCheckout.minAmount": ["Minimum amount (৳)", "0 = any amount"],
  "offerInterest.days": ["Within the last (days)", "Tried the yearly offer in this many days"],
  "packageExpiring.withinDays": ["Ends within (days)", "Package ends within this many days"],
  "verifiedNeverPaid.fromDays": ["From (days ago)", "Face-verified at least this many days ago"],
  "verifiedNeverPaid.toDays": ["To (days ago)", "…and at most this many days ago"],
  "engagedNeverPaid.activeDays": ["Active on at least (days)", "Days with app activity"],
  "engagedNeverPaid.withinDays": ["In the last (days)", "Counting activity in this many days"],
  "abandonedCheckout.maxAmount": ["Maximum amount (৳)", "0 = any amount"],
  "payers.withinDays": ["Paid in the last (days)", "Payments in this many days"],
  "payers.minAmount": ["Biggest payment from (৳)", "0 = any amount"],
  "payers.maxAmount": ["Biggest payment up to (৳)", "0 = any amount"],
  "packageEnded.withinDays": ["Ran out or ended within (days)", "Package used up or ended in this many days"],
  "members.onlineWithinDays": ["Online within (days)", "0 = every member"],
  "dormant.fromDays": ["Joined from (days ago)", "Joined at least this many days ago"],
  "dormant.toDays": ["Joined to (days ago)", "…and at most this many days ago"],
  "dormant.inactiveDays": ["Not online for (days)", "Last seen at least this many days ago"],
  "dormant.max": ["At most (members)", "A random sample this size; 0 = everyone"],
  minAmount: ["Minimum amount (৳)", ""],
  maxAmount: ["Maximum amount (৳)", "0 = any amount"],
  inactiveDays: ["Not online for (days)", ""],
  max: ["At most (members)", "0 = everyone"],
  fromHours: ["From (hours ago)", ""],
  toHours: ["To (hours ago)", ""],
  days: ["Days", ""],
  withinDays: ["Within (days)", ""],
  fromDays: ["From (days ago)", ""],
  toDays: ["To (days ago)", ""],
  activeDays: ["Active days", ""],
};
/** "abandonedCheckout" → "Abandoned checkout" */
export const humanize = (key) => {
  const words = String(key || "").replace(/([a-z0-9])([A-Z])/g, "$1 $2").replace(/[_-]+/g, " ").trim().toLowerCase();
  return words ? words.charAt(0).toUpperCase() + words.slice(1) : "";
};

/** Preset label from GET /marketing/settings presets, or a readable fallback. */
export const presetLabel = (presets, key) => (presets || []).find((p) => p.key === key)?.label || humanize(key);

export const presetOptionText = (preset, key) => {
  const t = PRESET_OPTION_TEXT[`${preset}.${key}`] || PRESET_OPTION_TEXT[key];
  return t ? { label: t[0], help: t[1] } : { label: reasonText(key) || key, help: "" };
};

/* ------------------------------------------------------------------ formatters */

export const formatBdt = (n) =>
  `৳${(Number(n) || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export const formatNumber = (n) => (Number(n) || 0).toLocaleString("en-US");

/** 0.123 → "12.3%" (payer rates come as fractions) */
export const pct = (fraction) => `${((Number(fraction) || 0) * 100).toFixed(1)}%`;

/** a/b as a percent string, or "" when b is 0 */
export const ratio = (a, b) => (Number(b) ? `${(((Number(a) || 0) / Number(b)) * 100).toFixed(1)}%` : "");

const validDate = (d) => {
  if (!d) return null;
  const date = new Date(d);
  return Number.isNaN(date.getTime()) ? null : date;
};

/** Date + time in Bangladesh time */
export const dhaka = (d) => {
  const date = validDate(d);
  return date ? date.toLocaleString("en-US", { timeZone: "Asia/Dhaka", dateStyle: "medium", timeStyle: "short" }) : "—";
};
/** Date + time with seconds (timelines) */
export const dhakaFull = (d) => {
  const date = validDate(d);
  return date ? date.toLocaleString("en-US", { timeZone: "Asia/Dhaka", dateStyle: "medium", timeStyle: "medium" }) : "—";
};
export const dhakaDate = (d) => {
  const date = validDate(d);
  return date ? date.toLocaleDateString("en-US", { timeZone: "Asia/Dhaka", dateStyle: "medium" }) : "—";
};
export const dhakaTime = (d) => {
  const date = validDate(d);
  return date ? date.toLocaleTimeString("en-US", { timeZone: "Asia/Dhaka", timeStyle: "medium" }) : "";
};

/** 125 → "2:05", 3725 → "1:02:05" */
export const formatDuration = (sec) => {
  const s = Math.max(0, Math.floor(Number(sec) || 0));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const r = String(s % 60).padStart(2, "0");
  return h ? `${h}:${String(m).padStart(2, "0")}:${r}` : `${m}:${r}`;
};

export const formatBytes = (n) => {
  const v = Number(n) || 0;
  if (v >= 1048576) return `${(v / 1048576).toFixed(1)} MB`;
  if (v >= 1024) return `${Math.round(v / 1024)} KB`;
  return `${v} B`;
};

/** Today in Bangladesh as YYYY-MM-DD, optionally shifted by whole days. */
export const bdDateString = (offsetDays = 0) =>
  new Date(Date.now() + offsetDays * 864e5).toLocaleDateString("en-CA", { timeZone: "Asia/Dhaka" });

/** ISO date → "YYYY-MM-DDTHH:mm" in Bangladesh time, for <input type="datetime-local"> */
export const isoToDhakaInput = (iso) => {
  const date = validDate(iso);
  if (!date) return "";
  return new Date(date.getTime() + 6 * 36e5).toISOString().slice(0, 16);
};
/** "YYYY-MM-DDTHH:mm" read as Bangladesh time → ISO string (or null) */
export const dhakaInputToIso = (value) => {
  if (!value) return null;
  const date = new Date(`${value.length === 16 ? `${value}:00` : value}+06:00`);
  return Number.isNaN(date.getTime()) ? null : date.toISOString();
};

/** Same rules as the backend's canonicalMobile: 01XXXXXXXXX (also accepts +880 / 880). */
export const canonicalMobile = (mobile) => {
  if (!mobile) return null;
  let digits = String(mobile).replace(/\D/g, "");
  if (digits.startsWith("880")) digits = digits.slice(2);
  if (digits.length === 10 && digits.startsWith("1")) digits = `0${digits}`;
  return /^01[3-9]\d{8}$/.test(digits) ? digits : null;
};

export const isObjectId = (v) => /^[a-f0-9]{24}$/i.test(String(v || "").trim());

/** Parsed JSON shown in a <pre>; never throws. */
export const prettyJson = (v) => {
  if (v === undefined || v === null) return "—";
  try {
    return JSON.stringify(v, null, 2);
  } catch {
    return String(v);
  }
};

/* ------------------------------------------------------------------ API */

const cleanParams = (params = {}) =>
  Object.fromEntries(Object.entries(params).filter(([, v]) => v !== undefined && v !== null && v !== ""));

/**
 * One request → { ok, data, body, message } or { ok:false, error, status }.
 * Never throws. `error` is ready to show.
 */
export async function request(method, url, { data, params, timeout } = {}) {
  try {
    const res = await axios.request({ method, url, data, params: params && cleanParams(params), timeout });
    const body = res.data || {};
    if (body.success === false) {
      return { ok: false, error: reasonText(body.error || body.msg) || "Request failed", body, status: res.status };
    }
    return { ok: true, data: body.data, body, message: body.message, status: res.status };
  } catch (e) {
    const status = e?.response?.status;
    const body = e?.response?.data;
    let error = body && typeof body === "object" ? reasonText(body.error || body.msg) : "";
    if (!error) {
      if (status === 403) error = "You don't have access to this";
      else if (e?.code === "ECONNABORTED") error = "The request took too long";
      else if (status) error = `Server error (${status})`;
      else error = "Could not reach the server";
    }
    return { ok: false, error, status, body };
  }
}

/** Reads a JSON error out of a Blob response (recordings are fetched as blobs). */
const blobError = async (blob, fallback) => {
  try {
    if (blob && typeof blob.text === "function") {
      const json = JSON.parse(await blob.text());
      return reasonText(json.error || json.msg) || fallback;
    }
  } catch {
    // not JSON
  }
  return fallback;
};

const uid = () => getUid();

export const aiCallApi = {
  overview: () => request("get", `/ai-calling/overview/${uid()}`),
  saveSettings: (settings) => request("put", `/ai-calling/settings/${uid()}`, { data: { settings } }),
  saveSip: (sip, applyNow) => request("put", `/ai-calling/sip/${uid()}`, { data: { sip, applyNow: !!applyNow } }),
  testSip: (sip) => request("post", `/ai-calling/sip/test/${uid()}`, { data: { sip }, timeout: 60000 }),
  reregister: (applyNow) => request("post", `/ai-calling/sip/reregister/${uid()}`, { data: { applyNow: !!applyNow } }),
  testCall: (mobile, script) => request("post", `/ai-calling/test-call/${uid()}`, { data: { mobile, ...(script ? { script } : {}) } }),
  hangup: (callId) => request("post", `/ai-calling/hangup/${callId}/${uid()}`),

  active: () => request("get", `/ai-calling/active/${uid()}`),
  calls: (params) => request("get", `/ai-calling/calls/${uid()}`, { params }),
  /** Call history "fresh start": hide calls before now (clear: true shows everything again). Nothing is deleted. */
  callsFreshStart: (clear = false) => request("post", `/ai-calling/calls/fresh-start/${uid()}`, { data: { clear } }),
  call: (callId) => request("get", `/ai-calling/call/${callId}/${uid()}`),

  campaigns: () => request("get", `/ai-calling/campaigns/${uid()}`),
  createCampaign: (campaign) => request("post", `/ai-calling/campaigns/${uid()}`, { data: { campaign } }),
  previewCampaign: (campaign) => request("post", `/ai-calling/campaigns/preview/${uid()}`, { data: { campaign } }),
  campaign: (id) => request("get", `/ai-calling/campaign/${id}/${uid()}`),
  updateCampaign: (id, campaign) => request("put", `/ai-calling/campaign/${id}/${uid()}`, { data: { campaign } }),
  deleteCampaign: (id) => request("delete", `/ai-calling/campaign/${id}/${uid()}`),
  /** action: "launch" | "pause" | "resume" | "stop" */
  campaignAction: (id, action) => request("post", `/ai-calling/campaign/${id}/${action}/${uid()}`),
  campaignFollowUps: (id, followUps) => request("post", `/ai-calling/campaign/${id}/followUps/${uid()}`, { data: { followUps } }),

  dnc: (params) => request("get", `/ai-calling/dnc/${uid()}`, { params }),
  addDnc: (mobile, reason) => request("post", `/ai-calling/dnc/${uid()}`, { data: { mobile, reason } }),
  removeDnc: (id) => request("delete", `/ai-calling/dnc/${id}/${uid()}`),

  /** { days } or { campaignId } */
  report: (params) => request("get", `/ai-calling/report/${uid()}`, { params }),
  marketing: () => request("get", `/marketing/settings/${uid()}`),

  // Ticket agent (BibahoBanglaBackend/TicketAgent, Docs/Backend/TICKET_AGENT.md)
  ticketAgent: () => request("get", `/ai-calling/ticket-agent/${uid()}`),
  ticketAgentRuns: (params) => request("get", `/ai-calling/ticket-agent/runs/${uid()}`, { params }),
  ticketAgentRun: (runId) => request("get", `/ai-calling/ticket-agent/run/${runId}/${uid()}`),
  /** action: "send" ({ text }) | "discard" | "retry" | "codeIssue" ({ status, note }) | "teamHandled" ({ undo }) */
  ticketAgentAction: (runId, action, data = {}) => request("post", `/ai-calling/ticket-agent/run/${runId}/${action}/${uid()}`, { data }),
  investigateTicket: (ticketId, note) => request("post", `/ai-calling/ticket-agent/investigate/${ticketId}/${uid()}`, { data: { note } }),

  /**
   * Recording file as a Blob (the server logs every play and download).
   * → { ok:true, blob, filename } | { ok:false, error }
   */
  recording: async (callId, download = false) => {
    const fallback = "Could not load the recording";
    try {
      const res = await axios.get(`/ai-calling/recording/${callId}/${uid()}${download ? "?download=1" : ""}`, {
        responseType: "blob",
      });
      const blob = res.data;
      const type = String(res.headers?.["content-type"] || blob?.type || "");
      if (type.includes("application/json")) return { ok: false, error: await blobError(blob, fallback) };
      const ext = type.includes("wav") ? "wav" : "mp3";
      return { ok: true, blob, filename: `aicall-${callId}.${ext}` };
    } catch (e) {
      const status = e?.response?.status;
      const error = await blobError(e?.response?.data, status === 403 ? "You don't have access to recordings" : fallback);
      return { ok: false, error };
    }
  },
};

/* ------------------------------------------------------------------ hooks */

/** GET /ai-calling/overview: settings, engine status, today, access. */
export const useAICallOverview = () => {
  const [overview, setOverview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const fetch = useCallback(async ({ quiet = false } = {}) => {
    if (!quiet) setLoading(true);
    const res = await aiCallApi.overview();
    if (res.ok) {
      setOverview(res.data);
      setError("");
    } else if (!quiet) {
      setError(res.error);
    }
    setLoading(false);
    return res;
  }, []);

  return { overview, setOverview, loading, error, fetch };
};

/** GET /ai-calling/campaigns (list, newest first). */
export const useAICallCampaigns = () => {
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const fetch = useCallback(async () => {
    setLoading(true);
    const res = await aiCallApi.campaigns();
    if (res.ok) {
      setCampaigns(Array.isArray(res.data) ? res.data : []);
      setError("");
    } else {
      setError(res.error);
    }
    setLoading(false);
    return res;
  }, []);

  return { campaigns, loading, error, fetch };
};

/** GET /marketing/settings: presets, defaults (holdout, attribution days), link style. */
export const useMarketingPresets = () => {
  const [state, setState] = useState({ loaded: false, presets: [], settings: null, error: "" });

  const fetch = useCallback(async () => {
    const res = await aiCallApi.marketing();
    if (res.ok) {
      setState({ loaded: true, presets: Array.isArray(res.body?.presets) ? res.body.presets : [], settings: res.data || null, error: "" });
    } else {
      setState({ loaded: true, presets: [], settings: null, error: res.error });
    }
    return res;
  }, []);

  return { ...state, fetch };
};

/** A ticking clock (ms) for live timers. */
export const useNow = (intervalMs = 1000) => {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), intervalMs);
    return () => clearInterval(id);
  }, [intervalMs]);
  return now;
};

/** Runs fn every `ms` while `active`; the latest fn is always used. */
export const useInterval = (fn, ms, active = true) => {
  const ref = useRef(fn);
  useEffect(() => {
    ref.current = fn;
  }, [fn]);
  useEffect(() => {
    if (!active || !ms) return undefined;
    const id = setInterval(() => ref.current && ref.current(), ms);
    return () => clearInterval(id);
  }, [ms, active]);
};

/**
 * Live AI-call events over the shared admin socket (services/socketClient.js).
 * Subscribes with "aicall:subscribe" (again after every reconnect), calls
 * onEvent(type, data, raw) for each "aicall:event", and on unmount sends
 * "aicall:unsubscribe" and removes only its own listeners.
 *
 * Event payloads are { type, data: {...}, at }; a flat { type, ... } also works.
 * Returns { connected, subscribed, unavailable }.
 */
export const useAICallEvents = (onEvent) => {
  const handlerRef = useRef(onEvent);
  const [status, setStatus] = useState({ connected: false, subscribed: false, unavailable: false });

  useEffect(() => {
    handlerRef.current = onEvent;
  }, [onEvent]);

  useEffect(() => {
    // connect() replaces an existing socket that is still connecting, so only call it when there is none
    if (!socketClient.socket) socketClient.connect();
    const socket = socketClient.socket;
    if (!socket) {
      setStatus({ connected: false, subscribed: false, unavailable: true });
      return undefined;
    }
    if (!socket.connected && socket.active === false) socket.connect();

    const onConnect = () => {
      setStatus((s) => ({ ...s, connected: true, unavailable: false }));
      socket.emit("aicall:subscribe");
    };
    const onDisconnect = () => setStatus({ connected: false, subscribed: false, unavailable: false });
    const onSubscribed = () => {
      setStatus({ connected: true, subscribed: true, unavailable: false });
      if (handlerRef.current) handlerRef.current("subscribed", {}, null);
    };
    const onMessage = (msg) => {
      if (!msg || typeof msg !== "object" || !msg.type) return;
      const data = msg.data && typeof msg.data === "object" ? msg.data : msg;
      if (handlerRef.current) handlerRef.current(msg.type, data, msg);
    };

    socket.on("connect", onConnect);
    socket.on("disconnect", onDisconnect);
    socket.on("aicall:subscribed", onSubscribed);
    socket.on("aicall:event", onMessage);
    if (socket.connected) onConnect();

    return () => {
      socket.off("connect", onConnect);
      socket.off("disconnect", onDisconnect);
      socket.off("aicall:subscribed", onSubscribed);
      socket.off("aicall:event", onMessage);
      if (socket.connected) socket.emit("aicall:unsubscribe");
    };
  }, []);

  return status;
};
